import React, { useEffect, useState } from 'react';
import AppLayout from '../components/AppLayout';
import { db } from '../firebase';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { useSchoolId } from '../lib/auth/schoolContext';
import RoleGate from '../components/RoleGate';
import { ROLES, ROLE_LABELS, getRoleLabel } from '../lib/auth/roles';
import { approveUserRegistration } from '../lib/functions';

export default function AdminUserApprovals() {
  const { schoolId } = useSchoolId();
  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(true);
  const [approving, setApproving] = useState(null);

  const load = async () => {
    if (!schoolId) return;
    setLoading(true);
    const snap = await getDocs(query(collection(db, 'users'), where('requestedSchoolId', '==', schoolId)));
    const users = snap.docs.map(d => ({ id: d.id, ...d.data() }));
    setPending(users.filter(u => u.isActive !== true));
    setLoading(false);
  };

  useEffect(() => { load(); }, [schoolId]);

  const approve = async (uid, role) => {
    try {
      setApproving(uid);
      await approveUserRegistration({ targetUid: uid, role });
      await load();
    } catch (e) {
      console.error(e);
      alert('Failed to approve user');
    } finally {
      setApproving(null);
    }
  };

  return (
    <AppLayout>
      <RoleGate allow={[ROLES.SEN_CO, ROLES.SENCO]} fallback={<div className="p-6 text-gray-600">Only Full Access users (SENCOs / Senior Leaders) can manage approvals.</div>}>
        <div className="max-w-5xl mx-auto px-4 py-8 space-y-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">User Approvals</h1>
            <p className="text-sm text-gray-600">Approve pending users requesting access to your school.</p>
          </div>
          <div className="bg-white rounded shadow overflow-hidden">
            <div className="px-4 py-3 border-b text-sm text-gray-700">{loading ? 'Loading…' : `${pending.length} pending`}</div>
            {loading ? (
              <div className="p-6 text-gray-500">Loading…</div>
            ) : pending.length === 0 ? (
              <div className="p-6 text-gray-500">No pending requests</div>
            ) : (
              <div className="divide-y">
                {pending.map(u => (
                  <div key={u.id} className="p-4 flex items-center justify-between gap-4">
                    <div>
                      <div className="font-medium text-gray-900">{u.email || u.id}</div>
                      <div className="text-sm text-gray-600">Requested role: {getRoleLabel(u.requestedRole || 'TEACHER')} • UID: <span className="font-mono text-xs">{u.id}</span></div>
                    </div>
                    <div className="flex items-center gap-2">
                      <select defaultValue={u.requestedRole || 'TEACHER'} id={`role-${u.id}`} className="border rounded p-2 text-sm">
                        <option value="TEACHER">{ROLE_LABELS.TEACHER}</option>
                        <option value="TA">{ROLE_LABELS.TA}</option>
                        <option value="SENCO">{ROLE_LABELS.SENCO}</option>
                      </select>
                      <button
                        onClick={() => approve(u.id, document.getElementById(`role-${u.id}`).value)}
                        disabled={approving === u.id}
                        className="px-3 py-2 bg-indigo-600 text-white rounded disabled:opacity-50"
                      >
                        {approving === u.id ? 'Approving…' : 'Approve'}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </RoleGate>
    </AppLayout>
  );
}

